import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  ronghuiNews,
  marketingDigital,
} from '@/mocks/insightsData';

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { visible, ref };
}

function InsightCard({
  icon,
  iconBg,
  title,
  desc,
  slug,
  visible,
  delay,
}: {
  icon: string;
  iconBg: string;
  title: string;
  desc: string;
  slug: string;
  visible: boolean;
  delay: number;
}) {
  const navigate = useNavigate();

  return (
    <a
      href={`/insights/article/${slug}`}
      onClick={(e) => { e.preventDefault(); navigate(`/insights/article/${slug}`); }}
      className={`group bg-surface rounded-xl p-5 border border-gray-100 hover:border-accent/30 hover:bg-surface-card transition-all duration-300 cursor-pointer flex flex-col h-full ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex items-start gap-3 flex-1">
        <div
          className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${iconBg}`}
        >
          <i className={`${icon} text-white`} />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-primary mb-1 group-hover:text-accent transition-colors">
            {title}
          </h4>
          <p className="text-xs text-text-muted leading-relaxed">{desc}</p>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs font-medium text-accent group-hover:text-accent-dark transition-colors">
          阅读全文
        </span>
        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 group-hover:bg-accent/10 transition-colors">
          <i className="ri-arrow-right-line text-xs text-gray-400 group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
        </div>
      </div>
    </a>
  );
}

export default function Insights() {
  const rnRef = useVisible(0.1);
  const mdRef = useVisible(0.1);

  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 bg-[#FAFAF8] overflow-hidden">
        {/* 极淡网格纹理 */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(#78716c 1px, transparent 1px), linear-gradient(90deg, #78716c 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        {/* 右上渐变光晕 */}
        <div
          className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full opacity-[0.08] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,174,239,0.3) 0%, transparent 70%)' }}
        />
        {/* 左下光晕 */}
        <div
          className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full opacity-[0.06] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(30,95,173,0.2) 0%, transparent 70%)' }}
        />
        {/* 装饰竖线 */}
        <div className="absolute top-[10%] left-[12%] w-px h-[300px] bg-gradient-to-b from-transparent via-[#1c1917]/5 to-transparent pointer-events-none" />
        <div className="absolute top-[15%] right-[15%] w-px h-[200px] bg-gradient-to-b from-transparent via-[#1c1917]/5 to-transparent pointer-events-none" />
        {/* 装饰横线 */}
        <div className="absolute top-[40%] left-[5%] w-[150px] h-px bg-gradient-to-r from-[#1c1917]/5 to-transparent pointer-events-none" />
        <div className="absolute bottom-[30%] right-[10%] w-[120px] h-px bg-gradient-to-l from-[#1c1917]/5 to-transparent pointer-events-none" />
        {/* 小圆点 */}
        <div className="absolute top-24 right-[30%] w-2 h-2 bg-[#00AEEF]/20 rounded-full animate-pulse pointer-events-none" />
        <div className="absolute bottom-32 left-[18%] w-1.5 h-1.5 bg-[#1c1917]/10 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
        {/* 装饰方块 */}
        <div className="absolute top-[20%] right-[8%] w-3 h-3 border border-[#00AEEF]/10 rounded-sm rotate-45 pointer-events-none" />
        <div className="absolute bottom-[25%] left-[6%] w-2 h-2 border border-[#1c1917]/10 rounded-sm rotate-45 pointer-events-none" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#1c1917] mb-4">
              融慧观察
            </h1>
            <p className="text-sm md:text-base text-[#78716c] leading-relaxed">
              资讯洞察x营销数字化前沿一线
            </p>
          </div>
        </div>
      </section>

      {/* 融慧资讯 */}
      <section ref={rnRef.ref} className="py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg">
                <i className="ri-newspaper-line text-emerald-600 text-lg" />
              </div>
              <h2
                className={`text-xl md:text-2xl font-bold text-primary transition-all duration-500 ${
                  rnRef.visible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                }`}
              >
                融慧资讯
              </h2>
            </div>
            <p className="text-sm text-text-muted">
              融慧数科官方公众号资讯专栏，聚焦行业趋势、标杆观点与实战动态
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {ronghuiNews.map((item, i) => (
              <InsightCard
                key={i}
                icon="ri-article-line"
                iconBg="bg-emerald-500"
                title={item.title}
                desc={item.desc}
                slug={item.slug}
                visible={rnRef.visible}
                delay={200 + i * 100}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 营销数字化 */}
      <section ref={mdRef.ref} className="py-16 md:py-24 bg-surface">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-lg">
                <i className="ri-bar-chart-box-line text-accent text-lg" />
              </div>
              <h2
                className={`text-xl md:text-2xl font-bold text-primary transition-all duration-500 ${
                  mdRef.visible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                }`}
              >
                营销数字化
              </h2>
            </div>
            <p className="text-sm text-text-muted">
              公众号「营销数字化」专栏精选，一物一码、bC联动、终端数字化的方法论与实战干货
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {marketingDigital.map((item, i) => (
              <InsightCard
                key={i}
                icon="ri-lightbulb-line"
                iconBg="bg-accent"
                title={item.title}
                desc={item.desc}
                slug={item.slug}
                visible={mdRef.visible}
                delay={200 + i * 100}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-surface-warm">
        <div className="w-full px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-xl md:text-3xl font-bold text-primary mb-4">
            获取更多行业洞察
          </h2>
          <p className="text-sm md:text-base text-text-muted mb-8 max-w-xl mx-auto">
            订阅我们的行业洞察，第一时间获取快消数字化最新趋势与实战案例。
          </p>
          <button
            onClick={() => {
              const el = document.createElement('div');
              el.className = 'fixed top-24 left-1/2 -translate-x-1/2 z-50 px-5 py-3 bg-primary text-white text-sm rounded-lg shadow-lg transition-all duration-300 pointer-events-none opacity-100';
              el.innerHTML = '<div class="flex items-center gap-2"><i class="ri-information-line"></i>订阅功能即将上线，敬请期待</div>';
              document.body.appendChild(el);
              setTimeout(() => {
                el.style.opacity = '0';
                setTimeout(() => el.remove(), 300);
              }, 2500);
            }}
            className="inline-flex items-center gap-2 px-8 py-3 bg-accent text-white font-semibold rounded-lg hover:bg-accent-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line" />
            订阅行业洞察
          </button>
        </div>
      </section>
    </main>
  );
}